import React, { useMemo, useRef, useState, useEffect, useContext, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiSearch, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import Swal from 'sweetalert2';
import withAuth from '../common/auth';
import {
  REPORT_CATEGORIES,
  userCanAccessReport,
} from './reportsConfig';
import { TabContext } from '../components/TabbedLayout';
import { ReportEmbedContext } from './reportEmbedContext';
import { getInlineReportComponent } from './reportInlineRoutes';
import { initializeWebUserInfo, getWebUserInfo } from '../utlilities/webConfig';

const buildReportPath = (categoryId, reportId) => `${categoryId}${reportId}`;

const isElectron = () =>
  typeof window !== 'undefined' && window.process && window.process.type;

let ipcRenderer;
if (isElectron()) {
  ipcRenderer = window.require('electron').ipcRenderer;
}

const processPermissions = (roles) => {
  const permissionsMap = {};
  const list = Array.isArray(roles) ? roles : [];
  list.forEach((role) => {
    (role.modules || []).forEach((module) => {
      permissionsMap[module.moduleName] = module.permissions;
    });
  });
  return permissionsMap;
};

const Reports = () => {
  const tabContext = useContext(TabContext);
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [permissions, setPermissions] = useState({});
  const [userInfo, setUserInfo] = useState({});
  const [accessReady, setAccessReady] = useState(false);

  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const [selectedReportPath, setSelectedReportPath] = useState(null);

  const sectionRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    const fetchAccess = async () => {
      let userRolesData;
      let userinfo;
      try {
        if (isElectron()) {
          userRolesData = await ipcRenderer.invoke('store-get', 'user_role');
          userinfo = await ipcRenderer.invoke('store-get', 'userInfo');
        } else {
          initializeWebUserInfo();
          userRolesData = getWebUserInfo().user_role;
          userinfo = getWebUserInfo().userInfo;
        }
      } catch {
        userRolesData = [];
        userinfo = {};
      }
      if (cancelled) return;
      setUserInfo(userinfo || {});
      setPermissions(processPermissions(userRolesData));
      setAccessReady(true);
    };
    fetchAccess();
    return () => {
      cancelled = true;
    };
  }, []);

  const categoriesVisible = useMemo(() => {
    if (!accessReady) return [];
    return REPORT_CATEGORIES.map((category) => ({
      ...category,
      reports: category.reports.filter((report) =>
        userCanAccessReport(userInfo, permissions, report)
      ),
    })).filter((category) => category.reports.length > 0);
  }, [accessReady, permissions, userInfo]);

  useEffect(() => {
    if (!accessReady) return;
    if (categoriesVisible.length === 0) {
      setSelectedReportPath(null);
      setExpandedCategoryId(null);
      return;
    }

    const pathStillAllowed = (path) => {
      if (!path) return false;
      for (const category of REPORT_CATEGORIES) {
        for (const report of category.reports) {
          if (buildReportPath(category.id, report.id) === path) {
            return userCanAccessReport(userInfo, permissions, report);
          }
        }
      }
      return false;
    };

    setSelectedReportPath((prev) => {
      if (pathStillAllowed(prev)) return prev;
      const first = categoriesVisible[0];
      return buildReportPath(first.id, first.reports[0].id);
    });

    setExpandedCategoryId((prev) => {
      if (prev && categoriesVisible.some((c) => c.id === prev)) return prev;
      return categoriesVisible[0]?.id ?? null;
    });
  }, [accessReady, categoriesVisible, permissions, userInfo]);

  const filteredCategories = useMemo(() => {
    if (!accessReady) return [];
    const base = categoriesVisible;

    if (!searchValue.trim()) {
      return base;
    }

    const query = searchValue.toLowerCase();

    return base
      .map((category) => {
        const filteredReports = category.reports.filter((report) => {
          return (
            report.name.toLowerCase().includes(query) ||
            report.description.toLowerCase().includes(query)
          );
        });

        if (
          category.title.toLowerCase().includes(query) ||
          category.description.toLowerCase().includes(query) ||
          filteredReports.length
        ) {
          return {
            ...category,
            reports: filteredReports.length ? filteredReports : category.reports,
          };
        }

        return {
          ...category,
          reports: filteredReports,
        };
      })
      .filter((category) => {
        if (category.title.toLowerCase().includes(query)) {
          return true;
        }
        if (category.description.toLowerCase().includes(query)) {
          return true;
        }
        return category.reports.length > 0;
      });
  }, [accessReady, categoriesVisible, searchValue]);

  const searchTerm = searchValue.trim();
  const isSearching = Boolean(searchTerm);

  useEffect(() => {
    const focus = searchParams.get('focus');
    if (focus && sectionRefs.current[focus]) {
      setExpandedCategoryId(focus);
      sectionRefs.current[focus].scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [searchParams, filteredCategories]);

  useEffect(() => {
    if (!isSearching) {
      return;
    }
    const firstWithReports = filteredCategories.find((c) => c.reports.length > 0);
    if (firstWithReports) {
      setExpandedCategoryId(firstWithReports.id);
    }
  }, [isSearching, filteredCategories]);

  const selectedMeta = useMemo(() => {
    for (const category of REPORT_CATEGORIES) {
      for (const report of category.reports) {
        const path = buildReportPath(category.id, report.id);
        if (path === selectedReportPath) {
          return { category, report };
        }
      }
    }
    return { category: null, report: null };
  }, [selectedReportPath]);

  const handleCategoryToggle = (categoryId) => {
    setExpandedCategoryId((prev) => (prev === categoryId ? null : categoryId));
  };

  const handleReportSelect = useCallback(
    (categoryId, report) => {
      if (!userCanAccessReport(userInfo, permissions, report)) {
        Swal.fire({
          title: 'Access Denied',
          text: "You don't have permission to view this report.",
          icon: 'error',
          confirmButtonColor: '#3085d6',
        });
        return;
      }
      const path = buildReportPath(categoryId, report.id);
      setSelectedReportPath(path);
      setExpandedCategoryId(categoryId);
      setSearchValue('');
    },
    [permissions, userInfo]
  );

  const ReportComponent = selectedReportPath
    ? getInlineReportComponent(selectedReportPath)
    : null;

  const canViewSelected =
    accessReady &&
    selectedMeta.report &&
    userCanAccessReport(userInfo, permissions, selectedMeta.report) &&
    ReportComponent;

  return (
    <div className="flex h-full min-h-0 bg-gradient-to-br from-slate-100 via-slate-50 to-indigo-50/40">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <header className="shrink-0 border-b border-slate-200/80 bg-white/90 px-4 py-3.5 shadow-[0_1px_0_rgba(15,23,42,0.04)] backdrop-blur-md sm:px-5">
          <div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-[15px] font-extrabold uppercase tracking-[0.12em] text-slate-900">
                Reports Center
              </h1>
              <p className="mt-0.5 text-xs text-slate-500">
                Browse categories and open a report in the workspace
              </p>
            </div>
            <div className="relative w-full sm:max-w-sm">
              <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={17} />
              <input
                type="search"
                placeholder="Search reports…"
                className="w-full rounded-xl border border-slate-200/90 bg-slate-50/80 py-2.5 pl-10 pr-4 text-sm text-slate-800 shadow-inner transition placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
              />
            </div>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-hidden p-2">
          <div className="flex h-full min-h-0 w-full min-w-0 flex-col gap-4 lg:flex-row lg:gap-5">
            <aside className="flex h-auto max-h-[42vh] w-full shrink-0 flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_4px_24px_-4px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/[0.03] lg:h-full lg:max-h-none lg:w-[min(100%,17.5rem)] xl:w-68">
              <div className="shrink-0 border-b border-slate-100 px-4 py-3.5">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-900">
                  Reports
                </p>
              </div>
              <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3" aria-label="Report categories">
                {!accessReady ? (
                  <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-3 py-8 text-center text-sm text-slate-500">
                    Loading permissions…
                  </p>
                ) : categoriesVisible.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-amber-200 bg-amber-50/80 px-3 py-8 text-center text-sm text-amber-900">
                    No reports are assigned for your role. Ask an administrator to grant report-related
                    module permissions (same as menu items such as Sales Entry, Purchase Entry).
                  </p>
                ) : filteredCategories.length > 0 ? (
                  filteredCategories.map((category) => {
                    const isOpen = expandedCategoryId === category.id;
                    return (
                      <div
                        key={category.id}
                        ref={(element) => {
                          sectionRefs.current[category.id] = element;
                        }}
                        className="mb-2.5 overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50/50 last:mb-0"
                      >
                        <button
                          type="button"
                          onClick={() => handleCategoryToggle(category.id)}
                          className={`flex w-full items-center gap-2 px-3.5 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                            isOpen
                              ? 'bg-white text-slate-900'
                              : 'text-slate-800 hover:bg-white/70'
                          }`}
                          aria-expanded={isOpen}
                        >
                          <span className="min-w-0 flex-1 text-[13px] font-semibold leading-snug">
                            {category.title}
                          </span>
                          <span className="shrink-0 rounded-md bg-slate-200/70 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-600">
                            {category.reports.length}
                          </span>
                          <span
                            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
                              isOpen
                                ? 'bg-indigo-100 text-indigo-700'
                                : 'bg-slate-200/60 text-slate-500'
                            }`}
                            aria-hidden
                          >
                            {isOpen ? (
                              <FiChevronDown size={16} strokeWidth={2.5} />
                            ) : (
                              <FiChevronRight size={16} strokeWidth={2.5} />
                            )}
                          </span>
                        </button>
                        {isOpen && (
                          <div className="border-t border-slate-100 bg-white px-1.5 py-2">
                            {category.reports.map((report) => {
                              const path = buildReportPath(category.id, report.id);
                              const isSelected = path === selectedReportPath;
                              return (
                                <button
                                  key={report.id}
                                  type="button"
                                  onClick={() => handleReportSelect(category.id, report)}
                                  className={`relative w-full rounded-lg py-2 pl-3 pr-2 text-left text-[13px] leading-snug transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset ${
                                    isSelected
                                      ? 'bg-indigo-50 font-semibold text-indigo-800 before:absolute before:left-0 before:top-1/2 before:h-[60%] before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-indigo-600'
                                      : 'font-medium text-indigo-700 hover:bg-slate-50 hover:text-indigo-800'
                                  }`}
                                >
                                  {report.name}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-3 py-8 text-center text-sm text-slate-500">
                    No reports match your search.
                  </p>
                )}
              </nav>
            </aside>

            <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_8px_40px_-12px_rgba(15,23,42,0.12)] ring-1 ring-slate-900/[0.03]">
              <div className="relative shrink-0 overflow-hidden border-b border-slate-100 bg-gradient-to-r from-white via-indigo-50/30 to-white px-4 py-3.5 sm:px-5">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-indigo-500/0 via-indigo-500/50 to-indigo-500/0" />
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-600/90">
                      {selectedMeta.category?.title ?? 'Report'}
                    </p>
                    <h2 className="mt-1 truncate text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                      {selectedMeta.report?.name ?? 'Select a report'}
                    </h2>
                  </div>
                  {selectedMeta.report?.description && (
                    <p className="max-w-xl text-xs leading-relaxed text-slate-600 sm:max-w-md sm:text-right sm:text-sm">
                      {selectedMeta.report.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-hidden bg-slate-50/40">
                {!accessReady ? (
                  <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-2 px-6 text-center">
                    <p className="text-sm font-medium text-slate-600">Loading…</p>
                  </div>
                ) : categoriesVisible.length === 0 ? (
                  <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-2 px-6 text-center">
                    <p className="text-sm font-medium text-slate-600">No reports available for your account.</p>
                  </div>
                ) : canViewSelected ? (
                  <ReportEmbedContext.Provider value={true}>
                    <div className="flex h-full min-h-[280px] flex-col overflow-auto bg-white">
                      <ReportComponent />
                    </div>
                  </ReportEmbedContext.Provider>
                ) : selectedMeta.report && !userCanAccessReport(userInfo, permissions, selectedMeta.report) ? (
                  <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-3 px-6 text-center">
                    <p className="max-w-sm text-sm font-medium text-slate-800">Access denied</p>
                    <p className="max-w-sm text-xs text-slate-600">
                      You do not have permission for module &quot;{selectedMeta.report.permissionModule}&quot;.
                    </p>
                  </div>
                ) : selectedReportPath ? (
                  <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-4 px-6 text-center">
                    <p className="max-w-sm text-sm text-slate-600">
                      This report is not available in the embedded viewer.
                    </p>
                    {tabContext?.openTab &&
                      selectedMeta.report &&
                      userCanAccessReport(userInfo, permissions, selectedMeta.report) && (
                        <button
                          type="button"
                          onClick={() =>
                            tabContext.openTab(selectedReportPath, selectedMeta.report.name)
                          }
                          className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition hover:bg-indigo-700"
                        >
                          Open in new tab
                        </button>
                      )}
                  </div>
                ) : (
                  <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-2 px-6 text-center">
                    <p className="text-sm font-medium text-slate-600">Choose a report</p>
                    <p className="max-w-xs text-xs text-slate-400">
                      Expand a category on the left, then select a report to load it here.
                    </p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default withAuth(Reports);
